import { forwardRef, useState } from 'react'
import type { InputHTMLAttributes } from 'react'

export interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string
  error?: string
  hint?: string
  /** When true, the password starts visible (text). User can still toggle it back. */
  defaultVisible?: boolean
}

/**
 * Password input with a toggle (eye) button to reveal the value. Forwards ref
 * for react-hook-form. Pass autoComplete="new-password" to discourage browser
 * auto-fill on settings forms.
 */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(props, ref) {
    const { label, error, hint, id, className, defaultVisible, ...rest } = props
    const [shown, setShown] = useState(defaultVisible ?? false)
    // Trick to defeat aggressive Chrome auto-fill: input starts readOnly, becomes
    // editable on focus. Browsers won't auto-populate readOnly inputs.
    const [readOnly, setReadOnly] = useState(true)
    const inputId = id ?? `pw-${label.replace(/\s+/g, '-').toLowerCase()}`

    return (
      <div className={className}>
        <label htmlFor={inputId} className="mb-1 block text-xs font-medium text-b4l-text-muted">
          {label}
        </label>
        <div className="relative">
          <input
            ref={ref}
            {...rest}
            id={inputId}
            type={shown ? 'text' : 'password'}
            readOnly={readOnly || rest.readOnly}
            onFocus={(e) => {
              setReadOnly(false)
              rest.onFocus?.(e)
            }}
            className="w-full rounded border border-b4l-border px-3 py-2 pr-10 outline-none focus:border-b4l-green disabled:bg-b4l-bg disabled:text-b4l-text-muted"
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShown((v) => !v)}
            aria-label={shown ? 'Ascunde parola' : 'Afișează parola'}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-b4l-text-muted hover:bg-b4l-bg hover:text-b4l-text"
          >
            {shown ? (
              // eye-off icon (when password is currently visible)
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                <line x1="2" x2="22" y1="2" y2="22" />
              </svg>
            ) : (
              // eye icon (when password is hidden)
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
        {hint && !error && <p className="mt-1 text-[11px] text-b4l-text-dim">{hint}</p>}
        {error && <p className="mt-1 text-xs text-b4l-red">{error}</p>}
      </div>
    )
  },
)
